import {
  ConnectionState,
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';
import type { LiveKitCredentials } from '../types/calls';

export type DirectSfuConnectionState = 'connected' | 'reconnecting' | 'disconnected';

interface DirectSfuMediaOptions {
  /** Relay-only ICE, same preference P2P and group calls honour. */
  protectIpAddress: boolean;
  /** The 1:1 provider's hidden <audio> sink, shared with the P2P path. */
  audioElement: HTMLAudioElement | null;
  outputDeviceId: string | null;
}

/**
 * Media for a 1:1 call the server routed through LiveKit ("direct SFU") so it
 * can be recorded. It is the P2P PeerService's counterpart for CallContext:
 * the 1:1 lifecycle, UI and call logging stay where they are, and only the
 * transport changes. Group calls keep their own room wiring in
 * GroupCallContext (tiles, speakers, add-participant), which this does not
 * need. There is exactly one remote participant: the peer.
 */
export class DirectSfuMedia {
  /** The peer's audio arrived (also after the peer reconnects). */
  onRemoteStream: ((stream: MediaStream | null) => void) | null = null;
  /** The peer left the room (crash, network loss, or hang-up). */
  onPeerLeft: (() => void) | null = null;
  /** This side's own room connection changed after the initial connect. */
  onConnectionStateChange: ((state: DirectSfuConnectionState) => void) | null = null;

  private readonly room: Room;
  private closed = false;
  // Connection changes are reported only once the first connect finished:
  // the initial Connected is the caller's cue to post `join`, not a recovery.
  private ready = false;
  private muted = false;
  private remoteTrack: RemoteTrack | null = null;

  constructor(private readonly options: DirectSfuMediaOptions) {
    this.room = new Room({ adaptiveStream: true, dynacast: true });
    this.room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        this.attach(track);
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (this.remoteTrack === track) this.detach();
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        if (this.closed) return;
        this.detach();
        this.onPeerLeft?.();
      })
      .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        if (this.closed || !this.ready) return;
        if (state === ConnectionState.Reconnecting) this.onConnectionStateChange?.('reconnecting');
        else if (state === ConnectionState.Connected) this.onConnectionStateChange?.('connected');
      })
      .on(RoomEvent.Disconnected, () => {
        if (this.closed || !this.ready) return;
        this.onConnectionStateChange?.('disconnected');
      });
  }

  /**
   * Join the call's room and publish the microphone. Throws on any failure.
   * `iceServers` are the app's own STUN/TURN (`/calls/ice-servers`): the SFU
   * offers no relay of its own, so relay-only ("protect IP") needs them.
   */
  async connect(credentials: LiveKitCredentials, muted: boolean, iceServers?: RTCIceServer[]): Promise<void> {
    if (!credentials.serverUrl || !credentials.participantToken) {
      throw new Error('Invalid LiveKit credentials');
    }
    this.muted = muted;
    // rtcConfig is a connect option in livekit-client (a Room option is
    // silently dropped).
    await this.room.connect(credentials.serverUrl, credentials.participantToken, {
      rtcConfig: {
        iceTransportPolicy: this.options.protectIpAddress ? 'relay' : 'all',
        ...(iceServers?.length ? { iceServers } : {}),
      },
    });
    if (this.closed) throw new Error('Direct call closed while connecting');
    // Reads the latest value: a mute toggled while connecting is kept.
    await this.room.localParticipant.setMicrophoneEnabled(!this.muted);
    this.ready = true;
    // A peer already in the room (the caller, for the callee) published
    // before this side subscribed to events for it.
    this.room.remoteParticipants.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.track) this.attach(publication.track as RemoteTrack);
      });
    });
  }

  setMuted(muted: boolean): Promise<void> {
    this.muted = muted;
    // Before the room is up, connect() applies the stored value.
    if (this.closed || !this.ready) return Promise.resolve();
    return this.room.localParticipant.setMicrophoneEnabled(!muted).then(() => undefined);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.detach();
    this.room.removeAllListeners();
    this.room.localParticipant.trackPublications.forEach((publication) => publication.track?.stop());
    void this.room.disconnect(true).catch(() => { /* local teardown is best-effort */ });
  }

  private attach(track: RemoteTrack): void {
    if (this.closed || track.kind !== Track.Kind.Audio) return;
    if (this.remoteTrack && this.remoteTrack !== track) this.detach();
    this.remoteTrack = track;
    const element = this.options.audioElement;
    if (element) {
      // attach() sets the stream and starts playback (the sink is autoPlay).
      track.attach(element);
      const sinkId = this.options.outputDeviceId;
      const setSinkId = (element as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId;
      if (sinkId && typeof setSinkId === 'function') {
        void setSinkId.call(element, sinkId).catch(() => { /* default speaker */ });
      }
    }
    const stream = typeof MediaStream !== 'undefined' && track.mediaStreamTrack
      ? new MediaStream([track.mediaStreamTrack])
      : null;
    this.onRemoteStream?.(stream);
  }

  private detach(): void {
    const track = this.remoteTrack;
    this.remoteTrack = null;
    if (!track) return;
    const element = this.options.audioElement;
    try {
      if (element) track.detach(element);
      else track.detach();
    } catch { /* already detached */ }
  }
}
